import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward,
  ZoomIn,
  ZoomOut,
  Scissors,
  RotateCcw,
  RotateCw,
  Monitor,
  Smartphone,
  Square,
  Trash2,
  MessageSquare,
  Target,
  Clock,
  FileVideo,
  X,
  ChevronRight
} from 'lucide-react';

const ProfessionalTimeline = ({ 
  duration = 0,
  currentTime = 0,
  onTimeChange,
  onPlay,
  onPause,
  isPlaying = false,
  clips = [],
  onClipSelect,
  onClipMove,
  onClipTrim,
  onClipSplit,
  onClipDelete,
  selectedClipIds = [],
  aspectRatio = '16:9',
  onAspectRatioChange,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null); // 'playhead', 'clip', 'trim-left', 'trim-right'
  const [dragClipId, setDragClipId] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  const [isMultiSelecting, setIsMultiSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [showSelectionPanel, setShowSelectionPanel] = useState(false);
  const [selectionContext, setSelectionContext] = useState(null);
  
  const timelineRef = useRef(null);
  const playheadRef = useRef(null);
  const tracksRef = useRef(null);

  // Timeline dimensions and scaling
  const rulerHeight = 40;
  const trackHeight = 80;
  const trackPadding = 8;
  const pixelsPerSecond = 50 * zoomLevel;
  const timelineWidth = Math.max(1000, duration * pixelsPerSecond);
  const totalHeight = rulerHeight + trackHeight + 20; // Extra space for waveforms

  // Time formatting
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }, []);

  // Generate time markers based on zoom level
  const timeMarkers = useMemo(() => {
    const markers = [];
    let interval = 10; // Default 10 second intervals
    
    if (zoomLevel >= 3) interval = 1;
    else if (zoomLevel >= 1.5) interval = 2;
    else if (zoomLevel >= 1) interval = 5;
    
    for (let time = 0; time <= duration; time += interval) {
      const x = time * pixelsPerSecond;
      const isMajor = time % (interval * 2) === 0 || interval === 1;
      
      markers.push(
        <div key={time} className="absolute">
          <div 
            className={`${isMajor ? 'bg-gray-300 h-6' : 'bg-gray-500 h-3'} w-px`}
            style={{ left: x }}
          />
          {isMajor && (
            <span 
              className="text-xs text-gray-400 mt-1 select-none absolute whitespace-nowrap"
              style={{ left: x - 20, top: 24 }}
            >
              {formatTime(time)}
            </span>
          )}
        </div>
      );
    }
    
    return markers;
  }, [duration, pixelsPerSecond, zoomLevel, formatTime]);

  // Calculate selection context when selected clips change
  useEffect(() => {
    if (selectedClipIds.length > 0) {
      const selectedClips = clips.filter(clip => selectedClipIds.includes(clip.id));
      const totalDuration = selectedClips.reduce((sum, clip) => sum + clip.duration, 0);
      const startTime = Math.min(...selectedClips.map(clip => clip.startTime));
      const endTime = Math.max(...selectedClips.map(clip => clip.startTime + clip.duration));
      const allTags = [...new Set(selectedClips.flatMap(clip => clip.tags || []))];
      
      setSelectionContext({
        clipCount: selectedClips.length,
        totalDuration,
        timeRange: { start: startTime, end: endTime },
        tags: allTags,
        clips: selectedClips.map(clip => ({
          id: clip.id,
          name: clip.name,
          duration: clip.duration,
          startTime: clip.startTime,
          tags: clip.tags || []
        }))
      });
      setShowSelectionPanel(selectedClips.length > 1); // Show panel for multi-selection
    } else {
      setSelectionContext(null);
      setShowSelectionPanel(false);
    }
  }, [selectedClipIds, clips]);

  // Handle timeline click for scrubbing
  const handleTimelineClick = useCallback((e) => {
    if (isDragging || !timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = Math.max(0, Math.min(clickX / pixelsPerSecond, duration));
    
    onTimeChange?.(newTime);
  }, [isDragging, pixelsPerSecond, duration, onTimeChange]);

  // Handle mouse down events
  const handleMouseDown = useCallback((e, type, clipId = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    setDragType(type);
    setDragClipId(clipId);
    setDragStartX(e.clientX);
    
    if (type === 'playhead') {
      setDragStartTime(currentTime);
    } else if (type === 'clip' && clipId) {
      const clip = clips.find(c => c.id === clipId);
      setDragStartTime(clip?.startTime || 0);
      
      // Handle multi-selection with Ctrl/Cmd key
      if (e.ctrlKey || e.metaKey) {
        const newSelected = selectedClipIds.includes(clipId) 
          ? selectedClipIds.filter(id => id !== clipId)
          : [...selectedClipIds, clipId];
        onClipSelect?.(newSelected);
      } else if (!selectedClipIds.includes(clipId)) {
        onClipSelect?.([clipId]);
      }
    }
  }, [currentTime, clips, selectedClipIds, onClipSelect]);

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStartX;
    const deltaTime = deltaX / pixelsPerSecond;
    
    if (dragType === 'playhead') {
      const newTime = Math.max(0, Math.min(dragStartTime + deltaTime, duration));
      onTimeChange?.(newTime);
    } else if (dragType === 'clip' && dragClipId) {
      const newStartTime = Math.max(0, dragStartTime + deltaTime);
      onClipMove?.(dragClipId, newStartTime);
    } else if (dragType === 'trim-left' && dragClipId) {
      const clip = clips.find(c => c.id === dragClipId);
      if (clip) {
        const newStart = Math.max(0, Math.min(clip.startTime + deltaTime, clip.endTime - 0.1));
        onClipTrim?.(dragClipId, newStart, clip.endTime);
      }
    } else if (dragType === 'trim-right' && dragClipId) {
      const clip = clips.find(c => c.id === dragClipId);
      if (clip) {
        const newEnd = Math.max(clip.startTime + 0.1, clip.startTime + deltaTime);
        onClipTrim?.(dragClipId, clip.startTime, newEnd);
      }
    }
  }, [isDragging, dragType, dragClipId, dragStartX, dragStartTime, pixelsPerSecond, duration, clips, onTimeChange, onClipMove, onClipTrim]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
    setDragClipId(null);
    setDragStartX(0);
    setDragStartTime(0);
  }, []);

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Quick selection shortcuts (defined before useEffect)
  const selectAllClips = useCallback(() => {
    onClipSelect?.(clips.map(clip => clip.id));
  }, [clips, onClipSelect]);

  const clearSelection = useCallback(() => {
    onClipSelect?.([]);
  }, [onClipSelect]);

  const selectClipsByTag = useCallback((tag) => {
    const taggedClips = clips.filter(clip => clip.tags?.includes(tag));
    onClipSelect?.(taggedClips.map(clip => clip.id));
  }, [clips, onClipSelect]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          isPlaying ? onPause?.() : onPlay?.();
          break;
        case 'Delete':
        case 'Backspace':
          if (selectedClipIds.length > 0) {
            selectedClipIds.forEach(clipId => onClipDelete?.(clipId));
          }
          break;
        case 's':
          if (selectedClipIds.length === 1) {
            const clip = clips.find(c => c.id === selectedClipIds[0]);
            if (clip) {
              const splitTime = currentTime - clip.startTime;
              if (splitTime > 0 && splitTime < clip.duration) {
                onClipSplit?.(selectedClipIds[0], splitTime);
              }
            }
          }
          break;
        case 'z':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (e.shiftKey) {
              onRedo?.();
            } else {
              onUndo?.();
            }
          }
          break;
        case 'a':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            selectAllClips();
          }
          break;
        case 'Escape':
          clearSelection();
          setShowSelectionPanel(false);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, selectedClipIds, clips, currentTime, onPlay, onPause, onClipDelete, onClipSplit, onUndo, onRedo, selectAllClips, clearSelection]);

  // Zoom controls
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.5, 8));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.5, 0.1));

  // Render waveform for a clip
  const renderWaveform = (clip) => {
    if (!clip.waveformData || clip.waveformData.length === 0) {
      return null;
    }

    const clipWidth = clip.duration * pixelsPerSecond;
    const waveformHeight = 20;
    const samplesPerPixel = clip.waveformData.length / clipWidth;
    
    const waveformPath = clip.waveformData.reduce((path, amplitude, index) => {
      const x = index / samplesPerPixel;
      const y = waveformHeight / 2 + (amplitude * waveformHeight / 4);
      return path + (index === 0 ? `M${x},${y}` : ` L${x},${y}`);
    }, '');

    return (
      <svg 
        className="absolute bottom-1 left-1 right-1 opacity-60 pointer-events-none"
        height={waveformHeight}
        style={{ width: clipWidth - 2 }}
      >
        <path
          d={waveformPath}
          stroke="#10b981"
          strokeWidth="1"
          fill="none"
        />
      </svg>
    );
  };

  const playheadPosition = currentTime * pixelsPerSecond;

  // Render selection range highlight
  const renderSelectionRange = () => {
    if (!selectionContext || selectedClipIds.length <= 1) return null;
    
    const { timeRange } = selectionContext;
    const startX = timeRange.start * pixelsPerSecond;
    const width = (timeRange.end - timeRange.start) * pixelsPerSecond;
    
    return (
      <div
        className="absolute bg-blue-500/20 border-l-2 border-r-2 border-blue-400 pointer-events-none z-15"
        style={{
          left: startX,
          top: rulerHeight,
          width: width,
          height: trackHeight
        }}
      >
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
          {selectedClipIds.length} clips selected
        </div>
      </div>
    );
  };


  return (
    <div className="w-full border-t border-zinc-700 bg-zinc-900">
      {/* Timeline Controls */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-700">
        <div className="flex items-center space-x-2">
          {/* Playback Controls */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onTimeChange?.(0)}
            className="text-gray-400 hover:text-white hover:bg-zinc-700"
          >
            <SkipBack className="w-4 h-4" />
          </Button>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={isPlaying ? onPause : onPlay}
            className="text-white hover:bg-zinc-700"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </Button>

          <div className="text-sm text-gray-400 mx-3 font-mono">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          {/* Undo/Redo */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onUndo}
            disabled={!canUndo}
            className="text-gray-400 hover:text-white hover:bg-zinc-700 disabled:opacity-30"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={onRedo}
            disabled={!canRedo}
            className="text-gray-400 hover:text-white hover:bg-zinc-700 disabled:opacity-30"
          >
            <RotateCw className="w-4 h-4" />
          </Button>

          {/* Split Tool */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (selectedClipIds.length === 1) {
                const clip = clips.find(c => c.id === selectedClipIds[0]);
                if (clip) {
                  const splitTime = currentTime - clip.startTime;
                  if (splitTime > 0 && splitTime < clip.duration) {
                    onClipSplit?.(selectedClipIds[0], splitTime);
                  }
                }
              }
            }}
            disabled={selectedClipIds.length !== 1}
            className="text-gray-400 hover:text-white hover:bg-zinc-700 disabled:opacity-30"
            title="Split at playhead (S)"
          >
            <Scissors className="w-4 h-4" />
          </Button>

          {/* Delete */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => selectedClipIds.forEach(clipId => onClipDelete?.(clipId))}
            disabled={selectedClipIds.length === 0}
            className="text-gray-400 hover:text-red-400 hover:bg-zinc-700 disabled:opacity-30"
            title="Delete selected (Del)"
          >
            <Trash2 className="w-4 h-4" />
          </Button>

          {/* Quick Selection Tools */}
          {clips.length > 0 && (
            <div className="flex items-center space-x-1 ml-4 border-l border-zinc-600 pl-4">
              <Button
                size="sm"
                variant="ghost"
                onClick={selectAllClips}
                className="text-gray-400 hover:text-white hover:bg-zinc-700 text-xs"
                title="Select all clips (Ctrl+A)"
              >
                All
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                disabled={selectedClipIds.length === 0}
                className="text-gray-400 hover:text-white hover:bg-zinc-700 text-xs disabled:opacity-30"
                title="Clear selection (Esc)"
              >
                Clear
              </Button>
            </div>
          )}

          {/* Selection Context Button */}
          {selectionContext && (
            <Button
              size="sm"
              variant={showSelectionPanel ? "default" : "ghost"}
              onClick={() => setShowSelectionPanel(!showSelectionPanel)}
              className="ml-2 text-blue-400 hover:text-white hover:bg-blue-600"
              title="Show selection context"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              {selectedClipIds.length}
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Aspect Ratio Toggle */}
          <div className="flex items-center space-x-1 bg-zinc-800 rounded p-1">
            <Button
              size="sm"
              variant={aspectRatio === '16:9' ? 'default' : 'ghost'}
              onClick={() => onAspectRatioChange?.('16:9')}
              className="h-7 px-2"
            >
              <Monitor className="w-3 h-3 mr-1" />
              16:9
            </Button>
            <Button
              size="sm"
              variant={aspectRatio === '9:16' ? 'default' : 'ghost'}
              onClick={() => onAspectRatioChange?.('9:16')}
              className="h-7 px-2"
            >
              <Smartphone className="w-3 h-3 mr-1" />
              9:16
            </Button>
            <Button
              size="sm"
              variant={aspectRatio === '1:1' ? 'default' : 'ghost'}
              onClick={() => onAspectRatioChange?.('1:1')}
              className="h-7 px-2"
            >
              <Square className="w-3 h-3 mr-1" />
              1:1
            </Button>
          </div>

          {/* Zoom Controls */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleZoomOut}
            className="text-gray-400 hover:text-white hover:bg-zinc-700"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          
          <span className="text-xs text-gray-400 mx-2 font-mono min-w-[3rem] text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={handleZoomIn}
            className="text-gray-400 hover:text-white hover:bg-zinc-700"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="relative overflow-x-auto overflow-y-hidden" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          ref={timelineRef}
          className="relative cursor-crosshair select-none"
          style={{ 
            width: timelineWidth,
            height: totalHeight,
            minWidth: '100%'
          }}
          onClick={handleTimelineClick}
        >
          {/* Time Ruler */}
          <div 
            className="absolute top-0 left-0 right-0 border-b border-zinc-600"
            style={{ height: rulerHeight, backgroundColor: '#1a1a1a' }}
          >
            {timeMarkers}
          </div>

          {/* Selection Range Highlight */}
          {renderSelectionRange()}

          {/* Video Track */}
          <div 
            ref={tracksRef}
            className="absolute left-0 right-0"
            style={{ 
              top: rulerHeight,
              height: trackHeight,
              backgroundColor: '#0f0f0f'
            }}
          >
            {/* Track Label */}
            <div className="absolute left-2 top-2 text-xs text-gray-400 font-medium z-10">
              Video Track
            </div>

            {/* Video Clips */}
            {clips.map((clip, index) => {
              const clipWidth = clip.duration * pixelsPerSecond;
              const clipLeft = clip.startTime * pixelsPerSecond;
              const isSelected = selectedClipIds.includes(clip.id);
              
              return (
                <div
                  key={clip.id}
                  className={`absolute rounded cursor-move transition-all group ${
                    isSelected
                      ? 'bg-blue-600 border-2 border-blue-400 shadow-lg z-20'
                      : 'bg-zinc-700 hover:bg-zinc-600 border border-zinc-600 z-10'
                  }`}
                  style={{
                    left: clipLeft,
                    top: trackPadding,
                    width: Math.max(clipWidth, 60),
                    height: trackHeight - (trackPadding * 2)
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'clip', clip.id)}
                >
                  {/* Clip Content */}
                  <div className="h-full flex flex-col justify-between p-2 pointer-events-none">
                    <div className="flex justify-between items-start">
                      <span className="text-xs text-white truncate font-medium">
                        {clip.name || `Clip ${index + 1}`}
                      </span>
                      {clip.tags && clip.tags.length > 0 && (
                        <div className="flex space-x-1">
                          {clip.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="text-[10px] bg-zinc-800 px-1 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-[10px] text-gray-300">
                      {formatTime(clip.duration)}
                    </div>
                  </div>

                  {/* Waveform */}
                  {renderWaveform(clip)}

                  {/* Trim Handles */}
                  {isSelected && (
                    <>
                      <div 
                        className="absolute left-0 top-0 bottom-0 w-2 bg-blue-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, 'trim-left', clip.id);
                        }}
                        style={{ borderRadius: '4px 0 0 4px' }}
                      />
                      <div 
                        className="absolute right-0 top-0 bottom-0 w-2 bg-blue-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, 'trim-right', clip.id);
                        }}
                        style={{ borderRadius: '0 4px 4px 0' }}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Playhead */}
          <div
            ref={playheadRef}
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 cursor-ew-resize z-30 pointer-events-auto"
            style={{ left: playheadPosition }}
            onMouseDown={(e) => handleMouseDown(e, 'playhead')}
          >
            {/* Playhead Handle */}
            <div 
              className="absolute -top-1 -left-2 w-4 h-3 bg-red-500 cursor-ew-resize hover:bg-red-400 transition-colors" 
              style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}
            />
            
            {/* Time Display */}
            <div
              className="absolute top-0 text-xs text-white bg-red-500 px-2 py-1 rounded-b font-mono pointer-events-none whitespace-nowrap"
              style={{ 
                left: Math.max(-30, -playheadPosition + 10),
                transform: playheadPosition < 60 ? 'translateX(0)' : 'translateX(-50%)'
              }}
            >
              {formatTime(currentTime)}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Info */}
      <div className="flex justify-between items-center p-2 text-xs text-gray-500 border-t border-zinc-800">
        <div className="flex space-x-4">
          <span>Click timeline to scrub</span>
          <span>Drag clips to move</span>
          <span>Ctrl+click for multi-select</span>
          <span>S to split at playhead</span>
          <span>Del to delete selected</span>
          <span>Ctrl+A to select all</span>
          <span>Esc to clear selection</span>
        </div>
        <div className="flex space-x-4">
          <span>{clips.length} clip{clips.length !== 1 ? 's' : ''}</span>
          <span>{selectedClipIds.length} selected</span>
        </div>
      </div>

      {/* Selection Context Panel */}
      {showSelectionPanel && selectionContext && (
        <div className="border-t border-zinc-700 bg-zinc-800">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="font-medium text-white">Selected Clips Context</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowSelectionPanel(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-zinc-900 p-3 rounded">
                <div className="text-xs text-gray-400">Clips</div>
                <div className="text-lg font-bold text-white">{selectionContext.clipCount}</div>
              </div>
              <div className="bg-zinc-900 p-3 rounded">
                <div className="text-xs text-gray-400">Duration</div>
                <div className="text-lg font-bold text-white">{formatTime(selectionContext.totalDuration)}</div>
              </div>
              <div className="bg-zinc-900 p-3 rounded">
                <div className="text-xs text-gray-400">Start</div>
                <div className="text-lg font-bold text-white">{formatTime(selectionContext.timeRange.start)}</div>
              </div>
              <div className="bg-zinc-900 p-3 rounded">
                <div className="text-xs text-gray-400">End</div>
                <div className="text-lg font-bold text-white">{formatTime(selectionContext.timeRange.end)}</div>
              </div>
            </div>

            {/* Tags */}
            {selectionContext.tags.length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-400 mb-2">Content Tags</div>
                <div className="flex flex-wrap gap-1">
                  {selectionContext.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-xs bg-blue-600 text-white px-2 py-1 rounded cursor-pointer hover:bg-blue-700"
                      onClick={() => selectClipsByTag(tag)}
                      title={`Click to select all "${tag}" clips`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Clips List */}
            <div>
              <div className="text-xs text-gray-400 mb-2">Selected Clips</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {selectionContext.clips.map((clip, index) => (
                  <div key={clip.id} className="flex items-center justify-between p-2 bg-zinc-900 rounded text-sm">
                    <div className="flex items-center space-x-2">
                      <FileVideo className="w-3 h-3 text-gray-400" />
                      <span className="text-white truncate">{clip.name || `Clip ${index + 1}`}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-xs text-gray-400">
                      <span>{formatTime(clip.duration)}</span>
                      <span>@{formatTime(clip.startTime)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-4 pt-3 border-t border-zinc-700">
              <div className="text-xs text-gray-400 mb-2">Quick Actions</div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Navigate to start of selection
                    onTimeChange?.(selectionContext.timeRange.start);
                  }}
                  className="text-xs"
                >
                  <ChevronRight className="w-3 h-3 mr-1" />
                  Go to Start
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    // Zoom to fit selection
                    const selectionDuration = selectionContext.timeRange.end - selectionContext.timeRange.start;
                    if (selectionDuration > 0) {
                      // Adjust zoom to fit selection (simplified)
                      const targetZoom = Math.min(8, Math.max(0.5, 1000 / (selectionDuration * 50)));
                      setZoomLevel(targetZoom);
                    }
                  }}
                  className="text-xs"
                >
                  <Target className="w-3 h-3 mr-1" />
                  Zoom to Fit
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalTimeline;